import type { z } from 'zod';

import {
  assignedPractitionerSchema,
  type AssignedPractitioner,
} from '@/features/booking/types/assignedPractitioner';
import type { SessionCategory } from '@/features/telephony/types/schemas';
import { apiClient } from '@/lib/api-client';
import { ApiError } from '@/lib/errors';
import { useAuthStore } from '@/stores/authStore';

import { parseBlock, type NdjsonBlock } from '../lib/ndjsonBlockTypes';
import {
  caseReportMetaSchema,
  caseReportSchema,
  clinicalBundleSchema,
  sessionFeedbackSchema,
  sessionMetricsResponseSchema,
  sessionNoteMetaSchema,
  sessionNoteSchema,
  sessionSchema,
  type CaseReport,
  type CaseReportMeta,
  type ClinicalBundle,
  type Concern,
  type PaginatedSessions,
  type Session,
  type SessionFeedback,
  type SessionListParams,
  type SessionMetricsParams,
  type SessionMetricsResponse,
  type SessionMode,
  type SessionNote,
  type SessionNoteMeta,
  type SessionStatus,
  type SessionType,
} from '../types';

export interface CreateSessionInput {
  clientId: string;
  practitionerId: string;
  /** ISO date, YYYY-MM-DD. */
  date: string;
  /** HH:MM, 15-minute aligned (:00, :15, :30, :45). */
  startTime: string;
  /** 30–150, multiple of 15. */
  durationMinutes: number;
  /** Optional. BE auto-categorizes new vs follow-up from history; honor 'crisis'. */
  sessionType?: SessionType;
  sessionMode: SessionMode;
  /** Online only. `portal_helpline` books a scheduled portal session; omit
   *  (or `campus_support`) for a campus booking. `in_person` is always
   *  campus_support BE-side. Instant calls use POST /portal-helpline/start,
   *  not this path. */
  projectKey?: 'campus_support' | 'portal_helpline';
  consentGiven: true;
  concerns?: Concern[];
  /** Phase 2 — free-text booking note. BE accepts the field today and will
   *  start storing it in a follow-up migration. ≤ 500 chars; surfaces on the
   *  Details step textarea. */
  note?: string;
}

/**
 * Payload for `POST /sessions/backfill`. Used to record a session that has
 * already happened (past time). No client notifications fire on this path.
 * `actualStart` / `actualEnd` are full ISO datetimes (UTC); `sessionMode` may
 * be `online`, `in_person`, or `helpline`. `projectKey`: `campus_support`
 * (online/in_person), `helpline_support` (helpline), or `portal_helpline`.
 * portal_helpline is online-only and must set `portalDelivery` (instant vs a
 * scheduled online call); the BE maps that to the row's bookingSource.
 */
export interface BackfillSessionInput {
  practitionerId: string;
  locationId: string;
  projectKey: 'campus_support' | 'helpline_support' | 'portal_helpline';
  sessionMode: SessionMode;
  /** portal_helpline only: was the recorded call instant click-to-call or a
   *  scheduled online appointment? Required when projectKey is portal_helpline. */
  portalDelivery?: 'instant' | 'online';
  /** helpline_support only: was the phone call inbound (client rang in) or
   *  outbound (practitioner rang the client)? Required when projectKey is
   *  helpline_support. Stored on session.metadata by the BE. */
  callDirection?: 'inbound' | 'outbound';
  /** Manual clinical flag. Unset → BE auto-classifies new vs follow-up. */
  sessionType?: SessionType;
  actualStart: string;
  actualEnd: string;
  clientId?: string;
  /** Required for backfill (BE: backfillOfflineSessionSchema enforces min 1). */
  concerns: string[];
}

/** Write payload for `POST /sessions/:id/notes` and `PATCH /sessions/:id/notes`.
 *  Body fields per `4_clinical-docs.md` §1: content is required on create
 *  and optional on update; clinicalData + transcriptJobId are always
 *  optional. clinicalData is freeform JSONB on the BE — typed permissively
 *  here so notes authored elsewhere (AI service) round-trip cleanly. */
export interface NoteWriteInput {
  content?: string;
  clinicalData?: Record<string, unknown> | null;
  transcriptJobId?: string | null;
}

export interface RescheduleSessionInput {
  date: string;
  startTime: string;
  durationMinutes: number;
  reason: string;
  /** Optional cross-practitioner reschedule — omit for same-practitioner. */
  practitionerId?: string;
  /** Optional cross-mode reschedule — omit for same-mode. */
  sessionMode?: SessionMode;
}

function parseOrThrow<T>(schema: z.ZodType<T>, raw: unknown, context: string): T {
  const result = schema.safeParse(raw);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path.join('.') ?? '';
    const detail = first
      ? `${path ? `${path}: ` : ''}${first.message}`
      : 'Unexpected response shape';
    if (import.meta.env.DEV) {
      console.error(`[sessionsService] ${context} response failed schema parse:`, {
        issues: result.error.issues,
        raw,
      });
    }
    throw new ApiError({
      status: 0,
      code: 'SHAPE_MISMATCH',
      message: `${context}: ${detail}`,
    });
  }
  return result.data;
}

/**
 * Lenient version of parseOrThrow for write endpoints (POST /sessions,
 * POST /sessions/backfill) where the BE has already created the row by
 * the time we get the 2xx response. A schema-parse failure on the
 * response body should NOT surface to the user as "Could not record
 * session" — the row IS in the DB and the list query will refetch via
 * invalidateQueries on success. We log loudly in DEV so drift gets
 * caught, but in production we trust the BE's 2xx and return the raw
 * payload cast to T.
 */
function parseOrAccept<T>(schema: z.ZodType<T>, raw: unknown, context: string): T {
  const result = schema.safeParse(raw);
  if (result.success) return result.data;
  if (import.meta.env.DEV) {
    console.error(
      `[sessionsService] ${context} response failed schema parse — accepting raw payload (write endpoint):`,
      { issues: result.error.issues, raw },
    );
  }
  return raw as T;
}

export const sessionsService = {
  /**
   * GET /sessions/practitioners?clientId=… — client-role-safe practitioner
   * discovery. Returns the list already filtered to the practitioners
   * assigned at the client's location. Do NOT call /practitioners/:id for
   * clients — it 403s (no practitioner:read).
   */
  async listAssignedPractitioners(clientId: string): Promise<AssignedPractitioner[]> {
    const { data } = await apiClient.get<{ data: unknown[] }>('/sessions/practitioners', {
      params: { clientId },
    });
    return data.data.map((row) =>
      parseOrThrow(assignedPractitionerSchema, row, 'listAssignedPractitioners'),
    );
  },

  async list(params: SessionListParams = {}): Promise<PaginatedSessions> {
    const { data } = await apiClient.get<{
      data: unknown[];
      pagination?: { page: number; limit: number; total: number };
    }>('/sessions', { params });
    const rows: Session[] = data.data.map((row) => parseOrThrow(sessionSchema, row, 'list'));
    return {
      data: rows,
      pagination: data.pagination ?? { page: 1, limit: rows.length, total: rows.length },
    };
  },

  /**
   * GET /sessions/metrics — role-scoped aggregate counts using the same filter
   * shape as `list` (minus pagination/sort/search). Powers the metrics row
   * above the admin session list.
   */
  async getMetrics(params: SessionMetricsParams = {}): Promise<SessionMetricsResponse> {
    const { data } = await apiClient.get<{ data: unknown }>('/sessions/metrics', { params });
    return parseOrThrow(sessionMetricsResponseSchema, data.data, 'getMetrics');
  },

  async get(id: string): Promise<Session> {
    const { data } = await apiClient.get<{ data: unknown }>(`/sessions/${id}`);
    return parseOrThrow(sessionSchema, data.data, 'get');
  },

  /**
   * GET /sessions/:id/clinical-bundle — snapshot of everything the
   * post-session screen needs (session metadata, case reports, session
   * note, persisted transcript, 1-hour presigned audio-only (M4A)
   * recording URLs, AI generation state). Throws ApiError on non-2xx.
   */
  async getClinicalBundle(sessionId: string, viewMode?: string): Promise<ClinicalBundle> {
    const { data } = await apiClient.get<{ data: unknown }>(
      `/sessions/${sessionId}/clinical-bundle`,
      viewMode !== undefined ? { params: { viewMode } } : undefined,
    );
    return parseOrThrow(clinicalBundleSchema, data.data, 'getClinicalBundle');
  },

  /**
   * Open the post-session AI summary SSE stream and dispatch typed events.
   * Uses fetch + ReadableStream (EventSource can't carry an Authorization
   * header). Returns when the stream closes naturally (server emits
   * `{kind:'done'}` or the response body ends). Throws on transport
   * errors and on non-2xx HTTP status.
   *
   * `getAuthToken` is read at fetch time so a reconnect after a 401
   * picks up the latest token from the auth store.
   */
  async streamSessionSummary(input: {
    streamUrl: string;
    handlers: {
      onNoteBlock: (block: NdjsonBlock) => void;
      onReportBlock: (block: NdjsonBlock) => void;
      onDone: () => void;
      onStreamError: (source: 'note' | 'report', message: string) => void;
    };
    signal?: AbortSignal;
    getAuthToken?: () => string | null;
  }): Promise<void> {
    const getToken = input.getAuthToken ?? (() => useAuthStore.getState().accessToken);
    const token = getToken();

    const res = await fetch(input.streamUrl, {
      headers: {
        Accept: 'text/event-stream',
        ...(token != null ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal: input.signal,
    });
    if (!res.ok || !res.body) {
      throw new Error(`SSE request failed: ${String(res.status)}`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      // Read loop terminates via `return` on `{kind:'done'}` or via `break`
      // when the underlying body stream signals end-of-stream.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let sepIdx = buffer.indexOf('\n\n');
        while (sepIdx >= 0) {
          const raw = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);
          const payload = raw
            .split('\n')
            .filter((l) => l.startsWith('data: '))
            .map((l) => l.slice(6))
            .join('\n');

          if (payload.length > 0) {
            let event: {
              kind?: string;
              line?: string;
              source?: 'note' | 'report';
              message?: string;
            };
            try {
              event = JSON.parse(payload) as typeof event;
            } catch {
              sepIdx = buffer.indexOf('\n\n');
              continue;
            }

            if (event.kind === 'note-line' && typeof event.line === 'string') {
              const block = parseBlock(event.line);
              if (block) input.handlers.onNoteBlock(block);
            } else if (event.kind === 'report-line' && typeof event.line === 'string') {
              const block = parseBlock(event.line);
              if (block) input.handlers.onReportBlock(block);
            } else if (event.kind === 'error') {
              input.handlers.onStreamError(event.source ?? 'note', event.message ?? '');
            } else if (event.kind === 'done') {
              input.handlers.onDone();
              return;
            }
            // note-done / report-done and any unknown future kinds:
            // server-side bookkeeping only — the bundle refetch on `done`
            // picks up the saved drafts.
          }

          sepIdx = buffer.indexOf('\n\n');
        }
      }
    } finally {
      reader.releaseLock();
    }
  },

  /** Returns null when no note exists — BE returns null in `data`, not 404. */
  async getNote(sessionId: string): Promise<SessionNote | null> {
    const { data } = await apiClient.get<{ data: unknown }>(`/sessions/${sessionId}/notes`);
    if (data.data === null || data.data === undefined) return null;
    return parseOrThrow(sessionNoteSchema, data.data, 'getNote');
  },

  /**
   * Timeline-only slim read. BE projects status + authorName + timestamps
   * (no content, no clinicalData) when `?meta=1` is set. Falls back to the
   * full payload silently if the BE hasn't deployed the projection yet —
   * zod strips unknown fields either way.
   */
  async getNoteMeta(sessionId: string): Promise<SessionNoteMeta | null> {
    const { data } = await apiClient.get<{ data: unknown }>(`/sessions/${sessionId}/notes`, {
      params: { meta: 1 },
    });
    if (data.data === null || data.data === undefined) return null;
    return parseOrThrow(sessionNoteMetaSchema, data.data, 'getNoteMeta');
  },

  /** Returns an array (empty when none — not a 404). */
  async listCaseReports(sessionId: string): Promise<CaseReport[]> {
    const { data } = await apiClient.get<{ data: unknown[] }>(
      `/sessions/${sessionId}/case-reports`,
    );
    return data.data.map((row) => parseOrThrow(caseReportSchema, row, 'listCaseReports'));
  },

  /**
   * Timeline-only slim read. BE projects status + actor display names +
   * timestamps when `?meta=1` is set, skipping the decrypt loop on
   * `content` / `rejectionReason`.
   */
  async listCaseReportMetas(sessionId: string, viewMode?: string): Promise<CaseReportMeta[]> {
    const { data } = await apiClient.get<{ data: unknown[] }>(
      `/sessions/${sessionId}/case-reports`,
      { params: { meta: 1, ...(viewMode !== undefined ? { viewMode } : {}) } },
    );
    return data.data.map((row) => parseOrThrow(caseReportMetaSchema, row, 'listCaseReportMetas'));
  },

  /** Returns an array (empty when none — not a 404). */
  async listFeedback(sessionId: string): Promise<SessionFeedback[]> {
    const { data } = await apiClient.get<{ data: unknown[] }>(`/sessions/${sessionId}/feedback`);
    return data.data.map((row) => parseOrThrow(sessionFeedbackSchema, row, 'listFeedback'));
  },

  async submitFeedback(
    sessionId: string,
    input: {
      ratings: { overall: number; heard: number; clarity: number; comfort: number };
      comment?: string;
    },
  ): Promise<SessionFeedback> {
    const body: Record<string, unknown> = { ratings: input.ratings };
    if (input.comment && input.comment.trim().length > 0) body.comment = input.comment;
    const { data } = await apiClient.post<{ data: unknown }>(
      `/sessions/${sessionId}/feedback`,
      body,
    );
    return parseOrThrow(sessionFeedbackSchema, data.data, 'sessionsService.submitFeedback');
  },

  async updateStatus(
    id: string,
    input: {
      status: SessionStatus;
      cancellationReason?: string;
      concerns?: string[];
      riskProfile?:
        | 'critical'
        | 'very_high'
        | 'high'
        | 'medium'
        | 'low'
        | 'very_low'
        | 'none'
        | 'unknown';
    },
  ): Promise<Session> {
    const body: Record<string, unknown> = { status: input.status };
    if (input.cancellationReason) body.cancellationReason = input.cancellationReason;
    if (input.concerns !== undefined) body.concerns = input.concerns;
    if (input.riskProfile !== undefined) body.riskProfile = input.riskProfile;
    const { data } = await apiClient.patch<{ data: unknown }>(`/sessions/${id}/status`, body);
    return parseOrThrow(sessionSchema, data.data, 'sessionsService.updateStatus');
  },

  /**
   * PATCH /sessions/:id/concerns — retag concerns on an already-completed
   * session. Separate endpoint from `updateStatus`: the BE restricts this one
   * to the session's clinical owner, their lead, or a chief at the location.
   * Gate the call site on `session.canEditConcerns` from the detail payload.
   */
  async updateConcerns(id: string, concerns: string[]): Promise<Session> {
    const { data } = await apiClient.patch<{ data: unknown }>(`/sessions/${id}/concerns`, {
      concerns,
    });
    return parseOrThrow(sessionSchema, data.data, 'sessionsService.updateConcerns');
  },

  /**
   * PATCH /sessions/:id with only `{ sessionCategory }`. Overrides the
   * automatic telephony classification on any status; the BE stamps the
   * overriding user so the call-ended rule never writes over it. Owner, BA
   * admins and chiefs only; a 403 surfaces as an ApiError.
   */
  async updateCategory({
    id,
    sessionCategory,
  }: {
    id: string;
    sessionCategory: SessionCategory;
  }): Promise<Session> {
    const { data } = await apiClient.patch<{ data: unknown }>(`/sessions/${id}`, {
      sessionCategory,
    });
    return parseOrThrow(sessionSchema, data.data, 'sessionsService.updateCategory');
  },

  async reschedule(id: string, input: RescheduleSessionInput): Promise<Session> {
    const { data } = await apiClient.post<{ data: unknown }>(`/sessions/${id}/reschedule`, input);
    return parseOrThrow(sessionSchema, data.data, 'sessionsService.reschedule');
  },

  async create(input: CreateSessionInput): Promise<Session> {
    const { data } = await apiClient.post<{ data: unknown }>('/sessions', input);
    // Lenient parse — the BE 2xx means the row is in the DB. A FE/BE schema
    // drift on the response body shouldn't surface as "Could not book".
    return parseOrAccept(sessionSchema, data.data, 'sessionsService.create');
  },

  /**
   * Record a session that has already happened (`actualStart` is in the past).
   * No client notifications fire on this path — the session lands in
   * `completed` status immediately. Used by the clinical "Book session" flow
   * when the practitioner picks a past date / time.
   */
  async backfill(input: BackfillSessionInput): Promise<Session> {
    const { data } = await apiClient.post<{ data: unknown }>('/sessions/backfill', input);
    return parseOrAccept(sessionSchema, data.data, 'sessionsService.backfill');
  },

  async createNote(sessionId: string, input: NoteWriteInput): Promise<SessionNote> {
    const { data } = await apiClient.post<{ data: unknown }>(`/sessions/${sessionId}/notes`, input);
    return parseOrThrow(sessionNoteSchema, data.data, 'sessionsService.createNote');
  },

  async updateNote(sessionId: string, input: NoteWriteInput): Promise<SessionNote> {
    const { data } = await apiClient.patch<{ data: unknown }>(
      `/sessions/${sessionId}/notes`,
      input,
    );
    return parseOrThrow(sessionNoteSchema, data.data, 'sessionsService.updateNote');
  },

  async submitNote(sessionId: string): Promise<SessionNote> {
    const { data } = await apiClient.post<{ data: unknown }>(`/sessions/${sessionId}/notes/submit`);
    return parseOrThrow(sessionNoteSchema, data.data, 'sessionsService.submitNote');
  },

  async createCaseReport(sessionId: string, input: { content: string }): Promise<CaseReport> {
    const { data } = await apiClient.post<{ data: unknown }>(
      `/sessions/${sessionId}/case-reports`,
      input,
    );
    return parseOrThrow(caseReportSchema, data.data, 'sessionsService.createCaseReport');
  },

  async updateCaseReport(
    sessionId: string,
    reportId: string,
    input: { content: string },
  ): Promise<CaseReport> {
    const { data } = await apiClient.patch<{ data: unknown }>(
      `/sessions/${sessionId}/case-reports/${reportId}`,
      input,
    );
    return parseOrThrow(caseReportSchema, data.data, 'sessionsService.updateCaseReport');
  },

  async submitCaseReport(sessionId: string, reportId: string): Promise<CaseReport> {
    const { data } = await apiClient.post<{ data: unknown }>(
      `/sessions/${sessionId}/case-reports/${reportId}/submit`,
    );
    return parseOrThrow(caseReportSchema, data.data, 'sessionsService.submitCaseReport');
  },

  async approveCaseReport(sessionId: string, reportId: string): Promise<CaseReport> {
    const { data } = await apiClient.post<{ data: unknown }>(
      `/sessions/${sessionId}/case-reports/${reportId}/approve`,
    );
    return parseOrThrow(caseReportSchema, data.data, 'sessionsService.approveCaseReport');
  },

  async rejectCaseReport(
    sessionId: string,
    reportId: string,
    input: { rejectionReason: string },
  ): Promise<CaseReport> {
    const { data } = await apiClient.post<{ data: unknown }>(
      `/sessions/${sessionId}/case-reports/${reportId}/reject`,
      input,
    );
    return parseOrThrow(caseReportSchema, data.data, 'sessionsService.rejectCaseReport');
  },
};
